/** Ephemeral realtime state (never persisted): presence, typing and live agent status. */
import type { AgentState, EphemeralEvent, Member, Presence } from "@bundle/contracts";
import type { AgentActivity } from "./agentStatus.ts";
import { addTyping, clearTyping, type TypingEntry } from "./typing.ts";

export type AgentLive = { state: AgentState; activity?: string; channelId?: string; at: number };

export type LiveState = {
  presence: Record<string, Presence>;
  typing: TypingEntry[];
  agents: Record<string, AgentLive>;
};

export function emptyLive(): LiveState {
  return { presence: {}, typing: [], agents: {} };
}

export function applyEphemeral(live: LiveState, ev: EphemeralEvent, meId: string, now: number): LiveState {
  switch (ev.type) {
    case "presence.changed":
      return live.presence[ev.userId] === ev.presence ? live : { ...live, presence: { ...live.presence, [ev.userId]: ev.presence } };
    case "typing":
      if (ev.userId === meId) return live;
      return { ...live, typing: addTyping(live.typing, ev.userId, ev.channelId, ev.threadRootId, now) };
    case "agent.state": {
      const a: AgentLive = { state: ev.state, at: now };
      if (ev.activity !== undefined) a.activity = ev.activity;
      if (ev.channelId !== undefined) a.channelId = ev.channelId;
      return { ...live, agents: { ...live.agents, [ev.userId]: a } };
    }
    // Shown (or not) by the platform's notifier (logic/notify); no live state.
    case "notification":
      return live;
    // Kept by the conversation it belongs to (ChannelViewModel ephemeral messages); no live state.
    case "app.ephemeral":
      return live;
    // Kept by the workspace's AppModalViewModel; no live state.
    case "app.views":
      return live;
    // Kept by the app's AppHomeViewModel (the Home tab of its DM); no live state.
    case "app.home":
      return live;
  }
}

/** From the `welcome` frame: listed users are active, everyone else away. */
export function applyOnline(live: LiveState, memberIds: string[], onlineUserIds: string[]): LiveState {
  const presence: Record<string, Presence> = {};
  for (const id of memberIds) presence[id] = "away";
  for (const id of onlineUserIds) presence[id] = "active";
  return { ...live, presence };
}

/** A message from `userId` ends their typing indicator in that channel. */
export function messageArrived(live: LiveState, userId: string, channelId: string): LiveState {
  const typing = clearTyping(live.typing, userId, channelId);
  return typing.length === live.typing.length ? live : { ...live, typing };
}

/** Live agent state wins over the (possibly stale) cached profile. */
export function agentState(live: LiveState, member: Member): { state: AgentState; activity?: string } {
  const a = live.agents[member.userId];
  if (a !== undefined) return a.activity === undefined ? { state: a.state } : { state: a.state, activity: a.activity };
  if (member.agent === undefined) return { state: "offline" };
  return member.agent.activity === undefined ? { state: member.agent.state } : { state: member.agent.state, activity: member.agent.activity };
}

/** Agent activity entries with names (sorted by user id), for agentStatusLines. */
export function agentActivities(live: LiveState, members: Record<string, Member>): AgentActivity[] {
  const out: AgentActivity[] = [];
  for (const userId of Object.keys(live.agents).toSorted()) {
    const a = live.agents[userId]!;
    const m = members[userId];
    const entry: AgentActivity = { userId, name: m === undefined ? userId : m.displayName, state: a.state, at: a.at };
    if (a.activity !== undefined) entry.activity = a.activity;
    if (a.channelId !== undefined) entry.channelId = a.channelId;
    out.push(entry);
  }
  return out;
}
